import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { trackSaveProgress } from '../utils/analytics';
import { getBackendUrl } from '../utils/apiBase';

const BACKEND_URL = getBackendUrl();

const pages = [
  { path: '/cover', number: 1 },
  { path: '/welcome', number: 2 },
  { path: '/operations', number: 3 },
  { path: '/customer', number: 4 },
  { path: '/marketing', number: 5 },
  { path: '/team', number: 6 },
  { path: '/roi', number: 7 },
  { path: '/matrix', number: 8 },
  { path: '/action', number: 9 },
];

export const NavigationControls = ({ currentPage, customNext }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData } = useAudit();

  const [saveEmail, setSaveEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Check if audit is completed (report generated)
  const auditCompleted = localStorage.getItem('audit_completed') === 'true';

  const currentIndex = pages.findIndex(p => p.number === currentPage);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < pages.length - 1;

  const goToPrev = () => {
    if (hasPrev) {
      navigate(auditPath(pages[currentIndex - 1].path, location.pathname));
      // Scroll to bottom of previous page after navigation
      setTimeout(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      // Use custom Next handler if provided, otherwise default
      if (customNext) {
        customNext();
      } else {
        navigate(auditPath(pages[currentIndex + 1].path, location.pathname));
        // Scroll to top of next page after navigation
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    }
  };

  const handleSaveProgress = async () => {
    if (!saveEmail || !saveEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/audit/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: saveEmail,
          audit_data: auditData,
          company: auditData.company || ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save progress');
      }

      const data = await response.json();

      // Track save event
      trackSaveProgress(saveEmail);

      toast.success(`Resume link sent to ${saveEmail}! Check your inbox.`);
      setDialogOpen(false);
      setSaveEmail('');

    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to send resume link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4" data-testid="navigation-controls">
      {/* MOBILE: Prev | Save | Next across */}
      <div className="flex sm:hidden items-center justify-between w-full gap-2">
        <Button
          variant="outline"
          onClick={goToPrev}
          disabled={!hasPrev}
          data-testid="nav-prev-button"
          className="gap-1 flex-1 text-sm"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Prev</span>
        </Button>

        {/* Save button - mobile - hide if audit completed */}
        {!auditCompleted && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1 text-xs border-2 border-accent text-accent-foreground hover:bg-accent/10 animate-pulse-border flex-1"
                data-testid="save-resume-button-mobile"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save & Resume Later</DialogTitle>
              <DialogDescription>
                We'll email you a secure link to resume your audit anytime within the next 7 days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4 mt-2">
              <div>
                <Label htmlFor="save-email">Your Email</Label>
                <Input
                  id="save-email"
                  type="email"
                  placeholder="your@email.com"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  data-testid="save-email-input"
                  className="mt-2"
                />
              </div>
              <Button
                onClick={handleSaveProgress}
                disabled={saving}
                className="w-full mt-4"
                data-testid="send-resume-link-button"
              >
                {saving ? 'Sending...' : 'Send Resume Link'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        )}

        <Button
          variant="default"
          onClick={goToNext}
          disabled={!hasNext}
          data-testid="nav-next-button"
          className="gap-1 flex-1 text-sm"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* DESKTOP: Original layout - Prev on left, Page dots + Save in center, Next on right */}
      <Button
        variant="outline"
        onClick={goToPrev}
        disabled={!hasPrev}
        data-testid="nav-prev-button-desktop"
        className="gap-2 hidden sm:flex"
      >
        <ChevronLeft className="h-4 w-4" />
        Previous
      </Button>

      <div className="hidden sm:flex items-center gap-3">
        {/* Page dots */}
        <div className="flex gap-1">
          {pages.map((page) => (
            <button
              key={page.number}
              onClick={() => {
                navigate(auditPath(page.path, location.pathname));
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className={`h-2 rounded-full transition-all ${
                page.number === currentPage
                  ? 'w-8 bg-primary'
                  : page.number < currentPage
                  ? 'w-2 bg-accent'
                  : 'w-2 bg-muted'
              }`}
              data-testid={`nav-dot-${page.number}`}
              aria-label={`Go to page ${page.number}`}
            />
          ))}
        </div>

        {/* Save button - desktop - hide if audit completed */}
        {!auditCompleted && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-xs border-2 border-accent text-accent-foreground hover:bg-accent/10 animate-pulse-border"
                data-testid="save-resume-button"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save & Resume Later</DialogTitle>
              <DialogDescription>
                We'll email you a secure link to resume your audit anytime within the next 7 days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4 mt-2">
              <div>
                <Label htmlFor="save-email">Your Email</Label>
                <Input
                  id="save-email"
                  type="email"
                  placeholder="your@email.com"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  data-testid="save-email-input"
                  className="mt-2"
                />
              </div>
              <Button
                onClick={handleSaveProgress}
                disabled={saving}
                className="w-full mt-4"
                data-testid="send-resume-link-button"
              >
                {saving ? 'Sending...' : 'Send Resume Link'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* DESKTOP: Next button on right */}
      <Button
        variant="default"
        onClick={goToNext}
        disabled={!hasNext}
        data-testid="nav-next-button-desktop"
        className="gap-2 hidden sm:flex"
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};